import { EditType, EditableCellProps } from "@components/EditableCell"
import { GraphNodeType } from "@custom-types/graphs"
import { ValidationError } from "@features/builder/graph/nodes/validation/types"
import { NodeProperties, NodeType } from "@features/builder/graph/types"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import PropertiesTable from "@features/graph/details/components/PropertiesTable"
import { FieldError, useFormContext } from "react-hook-form"

type CategoricalNodeTableProps = {
  nodeProperties: NodeProperties
  nodeLabelMap: Record<string, string>
  nodeTypeMap: Record<string, NodeType | GraphNodeType>
  onHeaderUpdate?: (oldName: string, newName: string) => void
  onCategoryRemove?: EditableCellProps["onRemove"]
  validationErrors?: ValidationError[]
}

const CategoricalNodeTable = (props: CategoricalNodeTableProps) => {
  const form = useFormContext<NodeFormValues>()
  const formErrors = form.formState.errors
  const parentIds = props.nodeProperties.parents_lst

  return (
    <PropertiesTable
      type={NodeType.Categorical}
      validationErrors={props.validationErrors}
      editable
      labelMap={props.nodeLabelMap}
      nodeTypeMap={props.nodeTypeMap}
      properties={props.nodeProperties}
      control={form.control}
      name="properties"
      customizeHeader={(header) => {
        const isParentNodeHeader = parentIds.includes(header.key)
        const isDistributionHeader = header.key === "distribution"

        if (isParentNodeHeader || isDistributionHeader) return header

        const updatedHeader = { ...header }
        updatedHeader.editable = !form.formState.disabled
        updatedHeader.type = EditType.Text
        updatedHeader.value = form.watch(`categoryDisplayNames.${header.key}`) ?? header.value
        updatedHeader.onBlur = (newValue) => props.onHeaderUpdate?.(header.key, newValue)
        updatedHeader.onRemove = props.onCategoryRemove

        return updatedHeader
      }}
      customizeRow={(row, rowIndex) => {
        const updatedRow = { ...row }

        for (const key of Object.keys(updatedRow)) {
          if (!updatedRow[key]) continue

          const isParameterKey = key !== "distribution" && !parentIds.includes(key)

          if (isParameterKey) {
            updatedRow[key].type = EditType.Number
            updatedRow[key].editOnClick = false
          }

          // overwrite editable if form is disabled
          if (form.formState.disabled) {
            updatedRow[key].editable = false
          }

          const rowError = formErrors.properties?.[rowIndex] as FieldError | undefined

          if (isParameterKey && rowError?.type === "categorical-parameters-sum") {
            updatedRow[key].errorMessage = rowError.message ?? ""
          } else {
            // @ts-expect-error TODO kk: invalid "key" types.
            // Currently they are typed as all possible form field names.
            // Instead it's typing should be narrowed down to "form.properties" field names only.
            // This is happening because type is set based on "control" of whole form.
            const error = rowError?.[key]
            updatedRow[key].errorMessage = error?.message ?? ""
          }

          // once form is submitted, we want to trigger validaiton to whole row on any cell change
          if (form.formState.isSubmitted) {
            updatedRow[key].onChange = () => {
              // next possible slot to run validation
              queueMicrotask(() => form.trigger(`properties.${rowIndex}`))
            }
          }
        }

        return updatedRow
      }}
    />
  )
}

export default CategoricalNodeTable
